/**
 * The LaTeX the Formula block understands, as data.
 *
 * A subset, and deliberately a *named* one: a creator writing `\alpha` gets an
 * alpha, a creator writing `\mathbb{R}` gets `\mathbb{R}` shown as written rather
 * than an empty gap (see `parse.ts`), and what is in this table is the whole of
 * what the block claims to typeset. Keeping it in one module means the parser,
 * the formula keyboard and the tests all agree on the same vocabulary instead of
 * each carrying its own idea of what is supported.
 *
 * The classes on the symbols are not decoration: TeX gives a relation (`=`, `≤`)
 * a medium space either side and a binary operator (`+`, `×`) the same, while a
 * punctuation mark (`,`) takes a thin space only *after* it. Typesetting a
 * formula without that spacing is what makes `a+b=c` read as a word rather than
 * as a sentence, so the table carries enough to tell the three apart.
 */

/** How much air TeX puts around a symbol, in ems. */
export const SPACE = {
  /** Nothing: a letter, a digit, a bracket. */
  none: 0,
  /** After a comma or a semicolon. */
  thin: 1 / 6,
  /** Around `=` and `≤`: relations. */
  medium: 2 / 9,
  /** After a full stop inside math. */
  thick: 5 / 18,
  /** An explicit `\,`. */
  explicitThin: 3 / 18,
  /** An explicit `\;`. */
  explicitThick: 5 / 18,
} as const

/** A symbol, and the air it carries with it. */
export type SymbolSpec = {
  /** Upright or italic? Letters are italic in maths; operators never are. */
  italic?: boolean
  before?: number
  after?: number
}

/** A command that typesets one character. */
export const SYMBOL_COMMANDS: Record<string, string> = {
  // Lower-case Greek
  alpha: 'α',
  beta: 'β',
  gamma: 'γ',
  delta: 'δ',
  epsilon: 'ε',
  varepsilon: 'ε',
  zeta: 'ζ',
  eta: 'η',
  theta: 'θ',
  vartheta: 'ϑ',
  iota: 'ι',
  kappa: 'κ',
  lambda: 'λ',
  mu: 'μ',
  nu: 'ν',
  xi: 'ξ',
  pi: 'π',
  rho: 'ρ',
  sigma: 'σ',
  tau: 'τ',
  upsilon: 'υ',
  phi: 'φ',
  varphi: 'φ',
  chi: 'χ',
  psi: 'ψ',
  omega: 'ω',
  // Upper-case Greek
  Gamma: 'Γ',
  Delta: 'Δ',
  Theta: 'Θ',
  Lambda: 'Λ',
  Xi: 'Ξ',
  Pi: 'Π',
  Sigma: 'Σ',
  Upsilon: 'Υ',
  Phi: 'Φ',
  Psi: 'Ψ',
  Omega: 'Ω',
  // Relations
  le: '≤',
  leq: '≤',
  ge: '≥',
  geq: '≥',
  ne: '≠',
  neq: '≠',
  approx: '≈',
  equiv: '≡',
  cong: '≅',
  sim: '∼',
  simeq: '≃',
  propto: '∝',
  ll: '≪',
  gg: '≫',
  prec: '≺',
  succ: '≻',
  // Binary operators
  times: '×',
  div: '÷',
  pm: '±',
  mp: '∓',
  cdot: '·',
  ast: '∗',
  star: '★',
  circ: '∘',
  bullet: '•',
  oplus: '⊕',
  otimes: '⊗',
  cap: '∩',
  cup: '∪',
  setminus: '\\',
  wedge: '∧',
  vee: '∨',
  land: '∧',
  lor: '∨',
  // Big operators
  sum: '∑',
  prod: '∏',
  int: '∫',
  iint: '∬',
  iiint: '∭',
  oint: '∮',
  // Arrows
  to: '→',
  rightarrow: '→',
  leftarrow: '←',
  leftrightarrow: '↔',
  Rightarrow: '⇒',
  Leftarrow: '⇐',
  Leftrightarrow: '⇔',
  implies: '⇒',
  iff: '⇔',
  uparrow: '↑',
  downarrow: '↓',
  mapsto: '↦',
  // Sets and logic
  in: '∈',
  notin: '∉',
  ni: '∋',
  subset: '⊂',
  subseteq: '⊆',
  supset: '⊃',
  supseteq: '⊇',
  emptyset: '∅',
  varnothing: '∅',
  forall: '∀',
  exists: '∃',
  nexists: '∄',
  neg: '¬',
  lnot: '¬',
  therefore: '∴',
  because: '∵',
  // Sundry
  infty: '∞',
  partial: '∂',
  nabla: '∇',
  hbar: 'ℏ',
  ell: 'ℓ',
  angle: '∠',
  perp: '⊥',
  parallel: '∥',
  degree: '°',
  prime: '′',
  dots: '…',
  ldots: '…',
  cdots: '⋯',
  vdots: '⋮',
  ddots: '⋱',
  Re: 'ℜ',
  Im: 'ℑ',
  aleph: 'ℵ',
  checkmark: '✓',
  dollar: '$',
  '%': '%',
  '&': '&',
  '#': '#',
  '_': '_',
  '{': '{',
  '}': '}',
  '\\': '\\',
  '|': '‖',
  lbrace: '{',
  rbrace: '}',
  langle: '⟨',
  rangle: '⟩',
  lceil: '⌈',
  rceil: '⌉',
  lfloor: '⌊',
  rfloor: '⌋',
  lvert: '|',
  rvert: '|',
  Vert: '‖',
  vert: '|',
}

/**
 * The spacing each *character* carries, where a creator typed the character
 * rather than the command for it.
 *
 * `−` (U+2212) is here as well as `-`, because the keyboard inserts the real
 * minus and a book pasted from elsewhere may carry either.
 */
export const CHAR_SPACING: Record<string, SymbolSpec> = {
  '=': {before: SPACE.medium, after: SPACE.medium},
  '<': {before: SPACE.medium, after: SPACE.medium},
  '>': {before: SPACE.medium, after: SPACE.medium},
  '≤': {before: SPACE.medium, after: SPACE.medium},
  '≥': {before: SPACE.medium, after: SPACE.medium},
  '≠': {before: SPACE.medium, after: SPACE.medium},
  '≈': {before: SPACE.medium, after: SPACE.medium},
  '≡': {before: SPACE.medium, after: SPACE.medium},
  '≅': {before: SPACE.medium, after: SPACE.medium},
  '∼': {before: SPACE.medium, after: SPACE.medium},
  '∝': {before: SPACE.medium, after: SPACE.medium},
  '→': {before: SPACE.medium, after: SPACE.medium},
  '←': {before: SPACE.medium, after: SPACE.medium},
  '↔': {before: SPACE.medium, after: SPACE.medium},
  '⇒': {before: SPACE.medium, after: SPACE.medium},
  '⇔': {before: SPACE.medium, after: SPACE.medium},
  '∈': {before: SPACE.medium, after: SPACE.medium},
  '∉': {before: SPACE.medium, after: SPACE.medium},
  '⊂': {before: SPACE.medium, after: SPACE.medium},
  '⊆': {before: SPACE.medium, after: SPACE.medium},
  '⊃': {before: SPACE.medium, after: SPACE.medium},
  '⊇': {before: SPACE.medium, after: SPACE.medium},
  '±': {before: SPACE.medium, after: SPACE.medium},
  '∓': {before: SPACE.medium, after: SPACE.medium},
  '+': {before: SPACE.medium, after: SPACE.medium},
  '−': {before: SPACE.medium, after: SPACE.medium},
  '-': {before: SPACE.medium, after: SPACE.medium},
  '×': {before: SPACE.medium, after: SPACE.medium},
  '÷': {before: SPACE.medium, after: SPACE.medium},
  '·': {before: SPACE.medium, after: SPACE.medium},
  '∗': {before: SPACE.medium, after: SPACE.medium},
  '∪': {before: SPACE.medium, after: SPACE.medium},
  '∩': {before: SPACE.medium, after: SPACE.medium},
  ',': {after: SPACE.thin},
  ';': {after: SPACE.thin},
  ':': {after: SPACE.thin},
  '∴': {before: SPACE.medium, after: SPACE.medium},
  '∵': {before: SPACE.medium, after: SPACE.medium},
  '⊥': {before: SPACE.medium, after: SPACE.medium},
  '∥': {before: SPACE.medium, after: SPACE.medium},
}

/** The command's spacing, for the ones whose character does not carry it. */
export const COMMAND_SPACING: Record<string, SymbolSpec> = {
  times: {before: SPACE.medium, after: SPACE.medium},
  div: {before: SPACE.medium, after: SPACE.medium},
  pm: {before: SPACE.medium, after: SPACE.medium},
  mp: {before: SPACE.medium, after: SPACE.medium},
  cdot: {before: SPACE.medium, after: SPACE.medium},
  ast: {before: SPACE.medium, after: SPACE.medium},
  star: {before: SPACE.medium, after: SPACE.medium},
  circ: {before: SPACE.medium, after: SPACE.medium},
  bullet: {before: SPACE.medium, after: SPACE.medium},
  oplus: {before: SPACE.medium, after: SPACE.medium},
  otimes: {before: SPACE.medium, after: SPACE.medium},
  cap: {before: SPACE.medium, after: SPACE.medium},
  cup: {before: SPACE.medium, after: SPACE.medium},
  setminus: {before: SPACE.medium, after: SPACE.medium},
  wedge: {before: SPACE.medium, after: SPACE.medium},
  vee: {before: SPACE.medium, after: SPACE.medium},
  land: {before: SPACE.medium, after: SPACE.medium},
  lor: {before: SPACE.medium, after: SPACE.medium},
  le: {before: SPACE.medium, after: SPACE.medium},
  leq: {before: SPACE.medium, after: SPACE.medium},
  ge: {before: SPACE.medium, after: SPACE.medium},
  geq: {before: SPACE.medium, after: SPACE.medium},
  ne: {before: SPACE.medium, after: SPACE.medium},
  neq: {before: SPACE.medium, after: SPACE.medium},
  approx: {before: SPACE.medium, after: SPACE.medium},
  equiv: {before: SPACE.medium, after: SPACE.medium},
  cong: {before: SPACE.medium, after: SPACE.medium},
  sim: {before: SPACE.medium, after: SPACE.medium},
  simeq: {before: SPACE.medium, after: SPACE.medium},
  propto: {before: SPACE.medium, after: SPACE.medium},
  ll: {before: SPACE.medium, after: SPACE.medium},
  gg: {before: SPACE.medium, after: SPACE.medium},
  prec: {before: SPACE.medium, after: SPACE.medium},
  succ: {before: SPACE.medium, after: SPACE.medium},
  in: {before: SPACE.medium, after: SPACE.medium},
  notin: {before: SPACE.medium, after: SPACE.medium},
  ni: {before: SPACE.medium, after: SPACE.medium},
  subset: {before: SPACE.medium, after: SPACE.medium},
  subseteq: {before: SPACE.medium, after: SPACE.medium},
  supset: {before: SPACE.medium, after: SPACE.medium},
  supseteq: {before: SPACE.medium, after: SPACE.medium},
  to: {before: SPACE.medium, after: SPACE.medium},
  rightarrow: {before: SPACE.medium, after: SPACE.medium},
  leftarrow: {before: SPACE.medium, after: SPACE.medium},
  leftrightarrow: {before: SPACE.medium, after: SPACE.medium},
  Rightarrow: {before: SPACE.medium, after: SPACE.medium},
  Leftarrow: {before: SPACE.medium, after: SPACE.medium},
  Leftrightarrow: {before: SPACE.medium, after: SPACE.medium},
  implies: {before: SPACE.medium, after: SPACE.medium},
  iff: {before: SPACE.medium, after: SPACE.medium},
  mapsto: {before: SPACE.medium, after: SPACE.medium},
  perp: {before: SPACE.medium, after: SPACE.medium},
  parallel: {before: SPACE.medium, after: SPACE.medium},
  therefore: {before: SPACE.medium, after: SPACE.medium},
  because: {before: SPACE.medium, after: SPACE.medium},
}

/** Explicit spaces a creator can type. */
export const SPACE_COMMANDS: Record<string, number> = {
  ',': SPACE.explicitThin,
  ':': SPACE.explicitThin / 1.5,
  ';': SPACE.explicitThick,
  '!': -SPACE.explicitThin,
  ' ': SPACE.thin,
  quad: 1,
  qquad: 2,
}

/**
 * Commands that print their argument upright, as a word rather than as maths.
 *
 * `\text{and}` must keep its spaces and must not be italicised — the difference
 * between `\text{if} x` and `\mathit{if} x` is the difference between a word and
 * three multiplied variables, and that is exactly the sort of thing a formula in
 * a school book depends on.
 */
export const TEXT_COMMANDS = new Set(['text', 'textrm', 'mbox', 'mathrm'])

/**
 * Function names, which are words in maths and take their argument upright.
 *
 * Apart from the spacing they carry, these are ordinary text: `\sin` is the three
 * letters s-i-n, and a book that prints them in italic is a book that has got
 * maths wrong.
 */
export const OP_NAME_COMMANDS = new Set([
  'sin',
  'cos',
  'tan',
  'cot',
  'sec',
  'csc',
  'arcsin',
  'arccos',
  'arctan',
  'sinh',
  'cosh',
  'tanh',
  'log',
  'ln',
  'lg',
  'exp',
  'lim',
  'max',
  'min',
  'sup',
  'inf',
  'det',
  'dim',
  'ker',
  'deg',
  'gcd',
  'bmod',
])

/**
 * The operators whose limits go *above and below* rather than beside them.
 *
 * This is what a display-style sum looks like, and it is the difference between
 * a textbook and a line of code: `\sum_{i=1}^{n}` has its bounds stacked over the
 * sigma, not tucked at its shoulder.
 */
export const LIMIT_OPERATOR_GLYPHS = new Set(['∑', '∏', '∐', '∫', '∬', '∭', '∮'])

export const LIMIT_OPERATOR_NAMES = new Set(['lim', 'max', 'min', 'sup', 'inf'])
